import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function ChangePassword() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError(t("pw.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(t("pw.done"));
      await logout();
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md kc-card p-8" data-testid="change-password-card">
        <div className="flex items-center gap-2 mb-6">
          <KeyRound className="w-6 h-6 text-primary" />
          <h1 className="text-3xl font-black uppercase tracking-tight">{t("pw.title")}</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("pw.current")}</label>
            <input data-testid="current-password-input" type="password" required value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="kc-input mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("pw.new")}</label>
            <input data-testid="new-password-input" type="password" required minLength={6} value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="kc-input mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("pw.confirm")}</label>
            <input data-testid="confirm-password-input" type="password" required minLength={6} value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="kc-input mt-1" />
          </div>
          {error && <p data-testid="change-password-error" className="text-sm text-destructive">{error}</p>}
          <button data-testid="change-password-submit" type="submit" disabled={loading} className="kc-btn-primary w-full">
            {loading ? t("common.loading") : t("pw.submit")}
          </button>
        </form>
        <Link data-testid="back-link" to={user?.role === "coach" ? "/coach" : "/app"}
          className="mt-5 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {t("forgot.back").replace("login", "").trim() || "Indietro"}
        </Link>
      </div>
    </div>
  );
}
